import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useUserLocation from '../hooks/useUserLocation';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Trees,
  Wrench,
  Calendar,
  Users,
  MapPin,
  Navigation,
  Loader2,
  Star,
  Clock,
  Route,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom colored marker factory
const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      background-color: ${color};
      width: 26px;
      height: 26px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "><div style="
      width: 8px;
      height: 8px;
      background: white;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    "></div></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -26],
  });
};

const markerColors = {
  parks: '#16a34a',
  services: '#2563eb',
  events: '#ea580c',
  organizations: '#7c3aed',
  user: '#ef4444',
};

// Component to recenter map when location changes
const RecenterMap = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, zoom || 12);
    }
  }, [position, zoom, map]);
  return null;
};

// Auto fit bounds to markers
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [map, positions]);
  return null;
};

const ExploreMap = () => {
  const navigate = useNavigate();
  const { location: userLocation, loading: locationLoading, requestLocation, hasLocation } = useUserLocation();
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState(['parks', 'services', 'events', 'organizations']);
  const [radius, setRadius] = useState('50');
  const [items, setItems] = useState({ parks: [], services: [], events: [], organizations: [] });

  const defaultCenter = [39.8283, -98.5795]; // US center
  const mapCenter = userLocation ? [userLocation.lat, userLocation.lng] : defaultCenter;
  const defaultZoom = userLocation ? 10 : 4;

  // Fetch all data on mount (global view)
  useEffect(() => {
    fetchGlobalData();
  }, []);

  // Re-fetch with location filter when location changes
  useEffect(() => {
    if (userLocation) {
      fetchNearbyData();
    }
  }, [userLocation, radius]);

  // Re-fetch when filters change (only if we have location)
  useEffect(() => {
    if (userLocation) {
      fetchNearbyData();
    } else {
      fetchGlobalData();
    }
  }, [activeFilters]);

  const fetchGlobalData = async () => {
    setLoading(true);
    try {
      const requests = [
        activeFilters.includes('parks') ? axios.get(`${API}/parks`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        activeFilters.includes('events') ? axios.get(`${API}/events`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ];
      const [parksRes, eventsRes] = await Promise.all(requests);
      setItems(prev => ({
        ...prev,
        parks: activeFilters.includes('parks') ? (parksRes.data || []) : [],
        events: activeFilters.includes('events') ? (eventsRes.data || []) : [],
      }));
    } catch (error) {
      console.error('Error fetching global data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyData = async () => {
    if (!userLocation) return;
    setLoading(true);
    try {
      const params = { lat: userLocation.lat, lng: userLocation.lng, radius_miles: parseFloat(radius), limit: 100 };
      const requests = [
        activeFilters.includes('parks') ? axios.get(`${API}/parks/nearby`, { params }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        activeFilters.includes('services') ? axios.get(`${API}/services/nearby`, { params }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        activeFilters.includes('events') ? axios.get(`${API}/events/nearby`, { params }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        activeFilters.includes('organizations') ? axios.get(`${API}/organizations/nearby`, { params }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ];
      const [parksRes, servicesRes, eventsRes, orgsRes] = await Promise.all(requests);
      setItems({
        parks: parksRes.data || [],
        services: servicesRes.data || [],
        events: eventsRes.data || [],
        organizations: orgsRes.data || [],
      });
    } catch (error) {
      console.error('Error fetching nearby data:', error);
      toast.error('Failed to load nearby data');
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (filter) => {
    setActiveFilters(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );
  };

  const filterButtons = [
    { id: 'parks', label: 'Dog Parks', icon: Trees, color: 'bg-green-600', textColor: 'text-green-700' },
    { id: 'services', label: 'Services', icon: Wrench, color: 'bg-blue-600', textColor: 'text-blue-700' },
    { id: 'events', label: 'Events', icon: Calendar, color: 'bg-orange-600', textColor: 'text-orange-700' },
    { id: 'organizations', label: 'Organizations', icon: Users, color: 'bg-violet-600', textColor: 'text-violet-700' },
  ];

  // Collect all valid marker positions for auto-fit
  const allPositions = [
    ...items.parks.filter(p => p.latitude && p.longitude).map(p => [p.latitude, p.longitude]),
    ...items.services.filter(s => s.latitude && s.longitude).map(s => [s.latitude, s.longitude]),
    ...items.events.filter(e => e.latitude && e.longitude).map(e => [e.latitude, e.longitude]),
    ...items.organizations.filter(o => o.latitude && o.longitude).map(o => [o.latitude, o.longitude]),
  ];

  const totalItems = items.parks.length + items.services.length + items.events.length + items.organizations.length;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Explore Map"
        description="Explore dog parks, services, events, and organizations near you on an interactive map."
        url="/map"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-700 to-amber-600 text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-heading font-medium mb-2 flex items-center gap-3">
            <Globe className="w-8 h-8" />
            Explore Map
          </h1>
          <p className="text-white/80">
            Discover dog parks, services, events, and organizations {hasLocation ? 'near you' : 'across the US'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Button
            onClick={requestLocation}
            disabled={locationLoading}
            variant={hasLocation ? 'outline' : 'default'}
            className="flex items-center gap-2"
            data-testid="map-use-location-btn"
          >
            {locationLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Navigation className="w-4 h-4" />
            )}
            {hasLocation ? 'Update Location' : 'Use My Location'}
          </Button>

          {hasLocation && (
            <Select value={radius} onValueChange={setRadius}>
              <SelectTrigger className="w-36" data-testid="radius-select">
                <SelectValue placeholder="Radius" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 miles</SelectItem>
                <SelectItem value="25">25 miles</SelectItem>
                <SelectItem value="50">50 miles</SelectItem>
                <SelectItem value="100">100 miles</SelectItem>
                <SelectItem value="250">250 miles</SelectItem>
              </SelectContent>
            </Select>
          )}

          <div className="h-6 w-px bg-border hidden sm:block" />

          {/* Filter toggles */}
          <div className="flex flex-wrap gap-2">
            {filterButtons.map(({ id, label, icon: Icon, color }) => (
              <Button
                key={id}
                variant={activeFilters.includes(id) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFilter(id)}
                className={activeFilters.includes(id) ? `${color} hover:opacity-90` : ''}
                data-testid={`filter-${id}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {label}
              </Button>
            ))}
          </div>

          {loading && <Loader2 className="w-5 h-5 animate-spin text-primary" />}

          <span className="text-sm text-muted-foreground ml-auto" data-testid="total-items-count">
            {totalItems} places found
          </span>
        </div>

        {/* Map */}
        <div className="rounded-lg overflow-hidden border shadow-sm" style={{ height: '600px' }} data-testid="explore-map">
          <MapContainer
            center={mapCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {userLocation ? (
              <RecenterMap position={mapCenter} zoom={10} />
            ) : allPositions.length > 1 ? (
              <FitBounds positions={allPositions} />
            ) : null}

            {/* User location marker */}
            {userLocation && (
              <>
                <Marker
                  position={mapCenter}
                  icon={createColoredIcon(markerColors.user)}
                >
                  <Popup><div className="font-medium">Your Location</div></Popup>
                </Marker>
                <Circle
                  center={mapCenter}
                  radius={parseFloat(radius) * 1609.34}
                  pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.05, weight: 1 }}
                />
              </>
            )}

            {/* Parks */}
            {activeFilters.includes('parks') && items.parks.map((park) => (
              park.latitude && park.longitude && (
                <Marker
                  key={`park-${park.id}`}
                  position={[park.latitude, park.longitude]}
                  icon={createColoredIcon(markerColors.parks)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="font-bold text-sm text-green-700 flex items-center gap-1">
                        <Trees className="w-4 h-4" />
                        {park.name}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {park.city}{park.state && `, ${park.state}`}
                      </p>
                      {(park.average_rating || park.rating) > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs">{(park.average_rating || park.rating).toFixed(1)}</span>
                        </div>
                      )}
                      {park.distance_miles != null && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          <Route className="w-3 h-3 mr-1" />{park.distance_miles} mi
                        </Badge>
                      )}
                      <button
                        onClick={() => navigate(`/parks/${park.id}`)}
                        className="block mt-2 text-xs text-green-600 font-medium hover:underline"
                      >
                        View Park Details &rarr;
                      </button>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}

            {/* Services */}
            {activeFilters.includes('services') && items.services.map((service) => (
              service.latitude && service.longitude && (
                <Marker
                  key={`service-${service.id}`}
                  position={[service.latitude, service.longitude]}
                  icon={createColoredIcon(markerColors.services)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="font-bold text-sm text-blue-700 flex items-center gap-1">
                        <Wrench className="w-4 h-4" />
                        {service.name}
                      </div>
                      <Badge variant="outline" className="mt-1 text-xs">{service.service_type}</Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {service.city}{service.state && `, ${service.state}`}
                      </p>
                      {service.distance_miles != null && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          <Route className="w-3 h-3 mr-1" />{service.distance_miles} mi
                        </Badge>
                      )}
                      <button
                        onClick={() => navigate(`/services/${service.id}`)}
                        className="block mt-2 text-xs text-blue-600 font-medium hover:underline"
                      >
                        View Service &rarr;
                      </button>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}

            {/* Events */}
            {activeFilters.includes('events') && items.events.map((event) => (
              event.latitude && event.longitude && (
                <Marker
                  key={`event-${event.id}`}
                  position={[event.latitude, event.longitude]}
                  icon={createColoredIcon(markerColors.events)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="font-bold text-sm text-orange-700 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {event.title}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{event.location}</p>
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {event.date} &middot; {event.time}
                      </div>
                      {event.distance_miles != null && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          <Route className="w-3 h-3 mr-1" />{event.distance_miles} mi
                        </Badge>
                      )}
                      {(event.external_url || event.external_link) && (
                        <a
                          href={event.external_url || event.external_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-1 text-xs text-orange-600 hover:underline"
                        >
                          Official Page &rarr;
                        </a>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )
            ))}

            {/* Organizations */}
            {activeFilters.includes('organizations') && items.organizations.map((org) => (
              org.latitude && org.longitude && (
                <Marker
                  key={`org-${org.id}`}
                  position={[org.latitude, org.longitude]}
                  icon={createColoredIcon(markerColors.organizations)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="font-bold text-sm text-violet-700 flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {org.name}
                      </div>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {org.organization_type?.replace('_', ' ')}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {org.city}{org.state && `, ${org.state}`}
                      </p>
                      {org.distance_miles != null && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          <Route className="w-3 h-3 mr-1" />{org.distance_miles} mi
                        </Badge>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
          {userLocation && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>You</span>
            </div>
          )}
          {filterButtons.map(({ id, label, color }) => (
            activeFilters.includes(id) && (
              <div key={id} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${color}`} />
                <span>{label}</span>
              </div>
            )
          ))}
          {!hasLocation && (
            <span className="ml-auto text-xs italic">
              <MapPin className="w-3 h-3 inline mr-1" />
              Enable location for nearby results with distance
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreMap;
